import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { L as Link } from "../lib/i18n";
import { useLang } from "../contexts/LanguageContext";
import { getRecentViews } from "../lib/recentViews";
import { Sparkles, BellRing, Smartphone, X } from "lucide-react";

const DISMISS_KEY = "carhive_guest_hint_dismissed_until_v1";
const DISMISS_COOLDOWN_DAYS = 14;
const MIN_VIEWS_TO_SHOW = 3;

const COPY = {
  de: {
    pill: "Empfehlung",
    title: "Speicher deine Auswahl",
    body: "Konto erstellen → Inserate geräteübergreifend speichern + Push für neue Treffer.",
    cta: "Registrieren",
    later: "Später",
    feat1: "Cross-Device",
    feat2: "Push-Benachrichtigungen",
    feat3: "Personalisiert",
  },
  fr: {
    pill: "Recommandation",
    title: "Sauvegarde ta sélection",
    body: "Créer un compte → annonces synchronisées entre appareils + alertes push pour nouveaux résultats.",
    cta: "S'inscrire",
    later: "Plus tard",
    feat1: "Multi-appareils",
    feat2: "Notifications push",
    feat3: "Personnalisé",
  },
  it: {
    pill: "Suggerimento",
    title: "Salva la tua selezione",
    body: "Crea un account → annunci sincronizzati su tutti i dispositivi + notifiche push per nuovi risultati.",
    cta: "Registrati",
    later: "Più tardi",
    feat1: "Multi-dispositivo",
    feat2: "Notifiche push",
    feat3: "Personalizzato",
  },
  en: {
    pill: "Tip",
    title: "Save your shortlist",
    body: "Create an account → listings synced across devices + push alerts for new matches.",
    cta: "Sign up",
    later: "Later",
    feat1: "Cross-device",
    feat2: "Push alerts",
    feat3: "Personalised",
  },
};

export default function GuestConversionHint() {
  const { user } = useAuth();
  const { lang } = useLang();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (user) { setVisible(false); return; }
    try {
      const until = parseInt(localStorage.getItem(DISMISS_KEY) || "0", 10);
      if (until && Date.now() < until) return;
    } catch { /* ignore */ }
    const recents = getRecentViews();
    if (recents.length >= MIN_VIEWS_TO_SHOW) setVisible(true);
  }, [user]);

  const snooze = () => {
    try {
      const until = Date.now() + DISMISS_COOLDOWN_DAYS * 24 * 60 * 60 * 1000;
      localStorage.setItem(DISMISS_KEY, String(until));
    } catch { /* ignore */ }
    setVisible(false);
  };

  if (!visible) return null;
  const t = COPY[lang] || COPY.de;

  return (
    <section className="mx-auto max-w-7xl px-4 pt-6 sm:px-6 lg:px-8" data-testid="guest-conversion-hint">
      <div className="relative overflow-hidden rounded-2xl border border-zinc-800 bg-gradient-to-br from-red-600/[0.08] via-zinc-950 to-zinc-950 p-5 sm:p-6">
        <div
          aria-hidden="true"
          className="pointer-events-none absolute -right-12 -top-12 h-48 w-48 rounded-full bg-red-500/10 blur-3xl"
        />
        <div className="relative grid gap-5 sm:grid-cols-[1fr_auto] sm:items-center">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-red-600/15 text-red-400">
                <Sparkles className="h-3.5 w-3.5" />
              </span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.22em] text-red-400">{t.pill}</span>
            </div>
            <h3 className="mt-3 font-display text-lg font-semibold text-white sm:text-xl">{t.title}</h3>
            <p className="mt-1 text-sm leading-relaxed text-zinc-400">{t.body}</p>
            <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5 text-[11px] text-zinc-500">
              <li className="inline-flex items-center gap-1"><Smartphone className="h-3 w-3 text-zinc-400" /> {t.feat1}</li>
              <li className="inline-flex items-center gap-1"><BellRing className="h-3 w-3 text-zinc-400" /> {t.feat2}</li>
              <li className="inline-flex items-center gap-1"><Sparkles className="h-3 w-3 text-zinc-400" /> {t.feat3}</li>
            </ul>
          </div>
          <div className="flex shrink-0 items-center gap-2">
            <Link
              to="/register"
              className="inline-flex items-center justify-center rounded-md bg-red-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-red-500"
              data-testid="guest-hint-register-btn"
            >
              {t.cta}
            </Link>
            <button
              type="button"
              onClick={snooze}
              className="rounded-md border border-zinc-800 px-3 py-2 text-xs text-zinc-400 hover:bg-zinc-900 hover:text-white"
              data-testid="guest-hint-snooze-btn"
            >
              {t.later}
            </button>
          </div>
        </div>
        <button
          type="button"
          onClick={snooze}
          title={t.later}
          aria-label={t.later}
          className="absolute right-2 top-2 rounded-md p-1.5 text-zinc-600 hover:bg-zinc-900 hover:text-zinc-300"
          data-testid="guest-hint-close"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      </div>
    </section>
  );
}
